/* ═══════════════════════════════════════════════════════════════════
   APPLICANT CATEGORY — single source of truth
   ───────────────────────────────────────────────────────────────────
   The category the applicant picks on the category bar ("Individual",
   "Entity", "Individual and Entity" …) decides BOTH which form is
   rendered AND the application fee, so it must never be inferred from
   a value that the applicant did not choose.

   Every category form also repeats the choice as a READ-ONLY row field
   ("(i) Category of the applicant") inside its applicant repeatable.
   That field used to be filled from the template's `defaultValue`, so a
   copy-pasted default in one category block ("Entity Applicant Details"
   carrying defaultValue "Individual") silently persisted the wrong
   category into every saved row — visible in the summary, and used
   as the fee fallback wherever the root value was missing.

   These helpers derive the value instead: from the applicant's actual
   selection, and — for combined categories that hold one repeatable per
   kind — from the repeatable's own section name. Nothing here hardcodes
   a category name; the kinds come from the template's categoryBar.
═══════════════════════════════════════════════════════════════════ */

export const DEFAULT_CATEGORY_FIELD = 'category_of_applicant';

const escRe = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* Whole-word, case-insensitive "does `text` name `term`?" — so "Entity"
   matches "Entity Applicant Details" but not "Entityless". */
const mentions = (text, term) => {
  if (!term) return false;
  try {
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escRe(term)}($|[^\\p{L}\\p{N}])`, 'iu').test(String(text || ''));
  } catch {
    return new RegExp(`(^|[^A-Za-z0-9])${escRe(term)}($|[^A-Za-z0-9])`, 'i').test(String(text || ''));
  }
};

/** Field name the template uses for the category (configurable, not assumed). */
export const getCategoryField = templateJson =>
  templateJson?.categoryBar?.field || DEFAULT_CATEGORY_FIELD;

/** Every category the template offers. */
export const getCategoryOptions = templateJson => {
  const bar = templateJson?.categoryBar?.options;
  if (Array.isArray(bar) && bar.length) return bar;
  return templateJson?.categoryForms ? Object.keys(templateJson.categoryForms) : [];
};

/** True for the read-only category field on a row (and its `_others` sibling). */
export const isCategoryKey = (key, field = DEFAULT_CATEGORY_FIELD) =>
  key === field || /^category_of/i.test(String(key || ''));

/* The indivisible applicant kinds. A combined option ("Individual and
   Entity") is recognised purely because it names another option inside
   itself, so this stays correct for any category list. */
export const atomicCategories = (options = []) => {
  const opts = (options || []).filter(Boolean);
  const atomic = opts.filter(o => !opts.some(other => other !== o && mentions(o, other)));
  return atomic.length ? atomic : opts;
};

/** The kind(s) a category covers: ["Entity"], or ["Individual","Entity"]. */
export const categoryParts = (category, options = []) => {
  if (!category) return [];
  const atoms = atomicCategories(options);
  if (atoms.includes(category)) return [category];
  const inside = atoms.filter(a => mentions(category, a));
  return inside.length ? inside : [category];
};

/** The applicant kind a repeatable section is for, read from its title. */
export const categoryFromSectionName = (sectionName, options = []) =>
  atomicCategories(options)
    .filter(a => mentions(sectionName, a))
    .sort((a, b) => b.length - a.length)[0] || '';

/**
 * The category a single repeatable row belongs to.
 *
 * - Single-kind category ⇒ every row is that category, full stop.
 * - Combined category ⇒ the kind named by the row's own section
 *   ("Entity Applicant Details" ⇒ "Entity"); when the section title
 *   names none, a plausible saved/template value is kept.
 */
export const resolveRowCategory = ({ sectionName = '', category = '', options = [], fallback = '' } = {}) => {
  if (!category) return fallback ? String(fallback) : '';
  const parts = categoryParts(category, options);
  if (parts.length === 1) return parts[0];
  const named = parts.filter(p => mentions(sectionName, p)).sort((a, b) => b.length - a.length);
  if (named.length) return named[0];
  if (fallback && parts.includes(String(fallback))) return String(fallback);
  return parts[0];
};

/** Row with its category field corrected. Returns the SAME object when already right. */
export const normalizeRowCategory = (row, { sectionName = '', category = '', options = [], field = DEFAULT_CATEGORY_FIELD } = {}) => {
  if (!row || typeof row !== 'object' || Array.isArray(row) || !category) return row;
  const keys = Object.keys(row).filter(k => isCategoryKey(k, field));
  if (!keys.length) return row;
  let changed = false;
  const out = { ...row };
  keys.forEach(k => {
    const want = resolveRowCategory({ sectionName, category, options, fallback: row[k] });
    if (want && out[k] !== want) { out[k] = want; changed = true; }
  });
  return changed ? out : row;
};

/** Same, for a whole repeatable. Returns the SAME array when nothing changed. */
export const normalizeRepeatableRows = (rows, ctx) => {
  if (!Array.isArray(rows) || !ctx?.category) return rows;
  let changed = false;
  const out = rows.map(r => { const n = normalizeRowCategory(r, ctx); if (n !== r) changed = true; return n; });
  return changed ? out : rows;
};

/**
 * The application's category, read back from saved templateData.
 *
 * Order matters: the root value is the one the applicant actually clicked,
 * so it always wins. Row values are the LAST resort and are cross-checked
 * against the section each row lives in, so a stale/mis-defaulted row can
 * no longer downgrade an Entity application to the Individual fee.
 */
export const resolveApplicantCategory = (data, options = [], field = DEFAULT_CATEGORY_FIELD) => {
  if (!data || typeof data !== 'object') return '';
  const ALL = data.ALL && typeof data.ALL === 'object' ? data.ALL : data;

  if (ALL[field]) return String(ALL[field]);

  // Underscore keys are saved METADATA, not answers — _fieldLabels in particular
  // holds `category_of_applicant: "(i) Category of the applicant"`, so reading it
  // as data would hand back a label where a category belongs.
  const isMeta = key => String(key || '').startsWith('_');

  for (const [key, val] of Object.entries(ALL)) {
    if (isMeta(key)) continue;
    if (val && typeof val === 'object' && !Array.isArray(val) && val[field]) return String(val[field]);
  }

  const kinds = [];
  Object.entries(ALL).forEach(([key, val]) => {
    if (isMeta(key) || !Array.isArray(val) || !val.length) return;
    // Only applicant repeatables carry the category field — ignore the rest
    // (biological resources, BMC rows …) so no unrelated title can vote.
    const rowWithCat = val.find(r => r && typeof r === 'object' && r[field]);
    if (!rowWithCat) return;
    const kind = categoryFromSectionName(key, options) || String(rowWithCat[field]);
    if (kind && !kinds.includes(kind)) kinds.push(kind);
  });

  if (!kinds.length) return '';
  if (kinds.length === 1) return kinds[0];
  const combined = (options || []).find(o => !kinds.includes(o) && kinds.every(k => mentions(o, k)));
  return combined || kinds[0];
};
